import { z } from "zod";
import {
  EventCursorSchema,
  HermesRuntimeIdSchema,
  HermesStoredIdSchema,
  HerSkinSessionIdSchema,
  IsoDateTimeSchema,
} from "./common.js";
import { MessageSchema } from "./message.js";
import { RunStatusSchema, RunSummarySchema } from "./run.js";
import { HermesProfileNameSchema } from "./hermes-profile.js";

export const SessionAttentionSchema = z.enum(["none", "unread"]);

function containsControlCharacter(value: string) {
  return [...value].some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
}

/**
 * Hermes 是存储会话的权威持久层。HerSkin overlay 只承接本地草稿和断线期间
 * 待同步的意图，并用 syncState 明确区分“已写 Hermes”和“尚待远端确认”。
 */
export const SessionLifecycleSchema = z.enum(["active", "archived", "deleted"]);
export const SessionTitleSourceSchema = z.enum(["hermes", "herskin_local"]);
export const SessionManagementStateSchema = z
  .object({
    lifecycle: SessionLifecycleSchema,
    revision: z.number().int().min(0),
    titleSource: SessionTitleSourceSchema,
    archivedAt: IsoDateTimeSchema.nullable(),
    deletedAt: IsoDateTimeSchema.nullable(),
    /** Hermes 是真实持久层；HerSkin overlay 只承接草稿与断线待同步动作。 */
    syncBoundary: z.literal("hermes_authoritative_with_herskin_overlay"),
    syncState: z.enum(["synced", "pending_remote", "local_only"]),
  })
  .strict();

/**
 * 仅服务端持久化与 Adapter 使用的绑定。浏览器 Session API 必须剥离 runtimeId、
 * storedId；Hermes 重启后旧 runtime generation 先进入 stale，再通过 storedId 水合。
 */
export const SessionBindingSchema = z
  .object({
    sessionId: HerSkinSessionIdSchema,
    adapterKind: z.literal("hermes"),
    profileName: HermesProfileNameSchema,
    runtimeId: HermesRuntimeIdSchema.nullable(),
    storedId: HermesStoredIdSchema.nullable(),
    runtimeGeneration: z.string().min(1).max(128).nullable(),
    bindingState: z.enum(["active", "stored_only", "stale", "missing"]),
    lastHydratedAt: IsoDateTimeSchema.nullable(),
    revision: z.number().int().min(0),
  })
  .strict();

export const SessionSummarySchema = z
  .object({
    id: HerSkinSessionIdSchema,
    /** 创建时绑定的 Hermes Profile；旧会话迁移为 default，之后不可改写。 */
    profileName: HermesProfileNameSchema,
    /** Hermes 稳定 stored session ID，可显示/复制；transient runtime ID 永不出服务端。 */
    agentSessionId: HermesStoredIdSchema.nullable(),
    title: z.string().min(1).max(200),
    preview: z.string().max(500),
    activeRunStatus: RunStatusSchema.nullable(),
    attention: SessionAttentionSchema,
    unreadCount: z.number().int().min(0).max(9999),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    /** 可选是为了兼容 M3 首版客户端；新服务端始终返回。 */
    management: SessionManagementStateSchema.optional(),
  })
  .strict();

export const SessionPageSchema = z
  .object({
    items: z.array(SessionSummarySchema),
    nextCursor: z.string().min(1).max(512).nullable(),
  })
  .strict();

export const CreateSessionRequestSchema = z
  .object({
    /** 浏览器创建的稳定 HerSkin ID；Hermes runtime ID 永不进入此字段。 */
    sessionId: HerSkinSessionIdSchema,
    profileName: HermesProfileNameSchema,
    title: z.string().min(1).max(200).default("新会话"),
  })
  .strict();

export const SessionListQuerySchema = z
  .object({
    view: z.enum(["active", "archived", "all"]).default("active"),
  })
  .strict();

export const RenameSessionRequestSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1)
      .max(100)
      .refine((value) => !containsControlCharacter(value), "标题不能包含控制字符"),
    baseRevision: z.number().int().min(0),
  })
  .strict();

const SessionRevisionRequestSchema = z
  .object({
    baseRevision: z.number().int().min(0),
  })
  .strict();

export const ArchiveSessionRequestSchema = SessionRevisionRequestSchema;
export const RestoreSessionRequestSchema = SessionRevisionRequestSchema;

export const DeleteSessionRequestSchema = z
  .object({
    baseRevision: z.number().int().min(0),
    confirm: z.literal("delete"),
  })
  .strict();

export const SessionMutationResponseSchema = z
  .object({
    action: z.enum(["renamed", "archived", "restored", "deleted"]),
    session: SessionSummarySchema.nullable(),
    management: SessionManagementStateSchema,
    remoteEffect: z.enum(["applied", "pending", "none"]),
    remoteSessionAffected: z.boolean(),
    alreadyApplied: z.boolean(),
  })
  .strict();

export const SessionSnapshotSchema = z
  .object({
    session: SessionSummarySchema,
    messages: z.array(MessageSchema).max(10_000),
    activeRun: RunSummarySchema.nullable(),
    revision: z.number().int().min(0),
    lastCursor: EventCursorSchema.nullable(),
    hydratedAt: IsoDateTimeSchema,
  })
  .strict();

export type SessionSummary = z.infer<typeof SessionSummarySchema>;
export type SessionLifecycle = z.infer<typeof SessionLifecycleSchema>;
export type SessionManagementState = z.infer<typeof SessionManagementStateSchema>;
export type SessionBinding = z.infer<typeof SessionBindingSchema>;
export type SessionPage = z.infer<typeof SessionPageSchema>;
export type CreateSessionRequest = z.infer<typeof CreateSessionRequestSchema>;
export type SessionListQuery = z.infer<typeof SessionListQuerySchema>;
export type RenameSessionRequest = z.infer<typeof RenameSessionRequestSchema>;
export type ArchiveSessionRequest = z.infer<typeof ArchiveSessionRequestSchema>;
export type RestoreSessionRequest = z.infer<typeof RestoreSessionRequestSchema>;
export type DeleteSessionRequest = z.infer<typeof DeleteSessionRequestSchema>;
export type SessionMutationResponse = z.infer<typeof SessionMutationResponseSchema>;
export type SessionSnapshot = z.infer<typeof SessionSnapshotSchema>;
